import { Link } from "wouter";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./reveal";
import { SaunaServices } from "./sauna-services";

const base = import.meta.env.BASE_URL;

const AC = [
  { href: "/installation", img: `${base}images/home-installation.webp`, pos: "center top", title: "Installation", desc: "Bespoke system design and precise installation using premium brands like Daikin and Mitsubishi." },
  { href: "/repair", img: `${base}images/home-repairs.webp`, pos: "center", title: "Repairs", desc: "Rapid diagnostics and durable repairs to get your systems back online with minimal downtime." },
  { href: "/maintenance", img: `${base}images/home-maintenance-gauges.webp`, pos: "center top", title: "Maintenance", desc: "Preventative servicing contracts to ensure efficiency, extend lifespan, and guarantee air quality." },
];

const HVAC = [
  { href: "/hvac/ventilation", img: `${import.meta.env.BASE_URL}images/ventilation-installation.webp`, title: "Ventilation", desc: "MVHR, MEV and extract systems for fresh, healthy indoor air." },
  { href: "/hvac/heating", img: `${import.meta.env.BASE_URL}images/heating-daikin.webp`, title: "Heating", desc: "Heat pumps, underfloor heating and modern heating solutions." },
  { href: "/hvac/ductwork", img: `${import.meta.env.BASE_URL}images/ductwork-installation.webp`, title: "Ductwork", desc: "Custom fabrication, installation and cleaning of duct systems." },
  { href: "/hvac/controls", img: `${import.meta.env.BASE_URL}images/bms-controls.webp`, title: "BMS & Controls", desc: "Intelligent building automation and energy management." },
];

export function Services() {
  return (
    <section id="services" className="py-14 lg:py-20 bg-[hsl(var(--vx-paper))]">
      <div className="container mx-auto max-w-7xl px-4">
        <Reveal className="grid lg:grid-cols-[1fr_1fr] gap-6 items-end mb-14">
          <div>
            <p className="vx-eyebrow text-[hsl(var(--vx-blue))] mb-4">01 — Services</p>
            <h2 className="vx-display text-4xl md:text-6xl font-bold text-[hsl(var(--vx-ink))]">Comprehensive climate solutions</h2>
          </div>
          <p className="text-lg text-slate-600 lg:max-w-md lg:justify-self-end">From single-room setups to complex commercial systems, our experienced engineers deliver uncompromising quality at every stage.</p>
        </Reveal>

        <div className="flex items-center gap-4 mb-6">
          <h3 className="font-display text-xl font-bold text-[hsl(var(--vx-ink))]">Air Conditioning</h3>
          <span className="flex-1 h-px bg-[hsl(var(--vx-mist))]" />
        </div>
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {AC.map((s, i) => (
            <Reveal key={s.href} delay={i * 0.08}>
              <Link href={s.href} className="group relative block h-[26rem] rounded-[1.75rem] overflow-hidden bg-[hsl(var(--vx-ink))] vx-lift" data-testid={`card-service-${s.title.toLowerCase()}`}>
                <img src={s.img} alt={s.title} width={800} height={600} loading="lazy" decoding="async" style={{ objectPosition: s.pos }}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--vx-ink))] via-[hsl(var(--vx-ink)/0.35)] to-transparent" />
                <span className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full bg-white/90 text-[hsl(var(--vx-ink))] transition-colors group-hover:bg-[hsl(var(--vx-ember))] group-hover:text-white">
                  <ArrowUpRight className="w-5 h-5" aria-hidden="true" />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <p className="vx-eyebrow text-[hsl(var(--vx-frost))] mb-2">0{i + 1}</p>
                  <h4 className="font-display text-3xl font-bold mb-2">{s.title}</h4>
                  <p className="text-white/75 leading-relaxed text-[0.95rem]">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="flex items-center gap-4 mb-6">
          <h3 className="font-display text-xl font-bold text-[hsl(var(--vx-ink))]">HVAC Systems</h3>
          <span className="flex-1 h-px bg-[hsl(var(--vx-mist))]" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HVAC.map((s, i) => (
            <Reveal key={s.href} delay={i * 0.06}>
              <Link href={s.href} className="group relative flex flex-col h-full rounded-[1.5rem] bg-white ring-1 ring-[hsl(var(--vx-mist))] overflow-hidden vx-lift transition-[box-shadow,ring-color] hover:ring-[hsl(var(--vx-blue)/0.35)]" data-testid={`card-hvac-${i}`}>
                <div className="relative h-44 sm:h-48 overflow-hidden bg-[hsl(var(--vx-ink))]">
                  <img src={s.img} alt={s.title} width={800} height={600} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--vx-ink)/0.7)] via-[hsl(var(--vx-ink)/0.1)] to-transparent" />
                  <span className="absolute top-3.5 left-3.5 rounded-full bg-[hsl(var(--vx-ink)/0.55)] backdrop-blur-md px-3 py-1 text-[0.7rem] font-semibold tracking-[0.16em] text-[hsl(var(--vx-frost))] ring-1 ring-white/15">
                    HVAC · 0{i + 1}
                  </span>
                  <span className="absolute bottom-3.5 right-3.5 grid place-items-center w-10 h-10 rounded-full bg-white/90 text-[hsl(var(--vx-ink))] shadow-lg transition-colors duration-300 group-hover:bg-[hsl(var(--vx-blue))] group-hover:text-white">
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 motion-safe:group-hover:translate-x-0.5 motion-safe:group-hover:-translate-y-0.5" aria-hidden="true" />
                  </span>
                </div>
                <div className="relative flex-1 p-5 pt-5">
                  <span aria-hidden="true" className="absolute left-5 top-0 h-[3px] w-10 rounded-b-full bg-[hsl(var(--vx-blue))] origin-left transition-transform duration-500 motion-safe:group-hover:scale-x-[2.2]" />
                  <h4 className="font-display text-xl font-bold tracking-tight text-[hsl(var(--vx-ink))] mb-1.5">{s.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <SaunaServices />
      </div>
    </section>
  );
}
